import React, { useEffect } from 'react';
import { Mail, Lock, User, Building2, ArrowLeft } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useSignupFormStore } from '@/features/auth/stores/signup-form.store';
import { toast } from 'react-hot-toast';
import { logger } from '@/features/help/utils/logger';
import { HelpButton } from '@/features/help/components/HelpButton';

export const SignupPage = () => {
  const navigate = useNavigate();

  const {
    name,
    email,
    password,
    companyName,
    agreedToTerms,
    setName,
    setEmail,
    setPassword,
    setCompanyName,
    setAgreedToTerms,
    resetForm,
  } = useSignupFormStore();

  const { register, isLoading: authIsLoading, error: authError, clearAuthError } = useAuthStore();

  useEffect(() => {
    clearAuthError();
  }, [clearAuthError]);

  const handleLegalLinkClick = (path: string) => {
    navigate(path);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (!companyName.trim()) {
      toast.error('Company name is required');
      return;
    }

    if (!agreedToTerms) {
      toast.error('You must agree to the terms of service to continue');
      return;
    }

    try {
      logger.debug('SignupPage', 'Starting registration process...', {
        debugId: 'registration-start',
        userId: 'system',
      });
      const firebaseUser = await register(name, companyName, email, password);

      if (firebaseUser) {
        resetForm();
        navigate('/verify-email');
      } else {
        if (useAuthStore.getState().error) {
          toast.error(useAuthStore.getState().error || 'Registration failed. Please try again.');
        } else {
          toast.error('Registration failed. Please try again.');
        }
      }
    } catch (error: any) {
      const currentAuthError = useAuthStore.getState().error;
      const message =
        currentAuthError || error.message || 'Failed to create account. Please try again.';
      toast.error(message);
      logger.error('SignupPage', 'Registration failed', {
        error: error,
        authStoreError: currentAuthError,
        fullError: error,
      });
    }
  };

  return (
    <div className="min-h-screen bg-white font-inter flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative">
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 text-gray-600 hover:text-gray-900 transition-colors"
        aria-label="Go back"
      >
        <ArrowLeft className="h-6 w-6" />
      </button>

      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl border border-blue-200 shadow-2xl shadow-blue-500/30 ring-4 ring-blue-300/20 relative before:absolute before:inset-0 before:rounded-2xl before:bg-gradient-to-r before:from-blue-400/20 before:to-purple-400/20 before:blur-xl before:-z-10">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Create your account
          </h2>
          {authError && <p className="mt-2 text-center text-sm text-red-600">{authError}</p>}
          <p className="mt-2 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-blue-500 hover:text-blue-600">
              Sign in
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            <div className="relative">
              <label htmlFor="name" className="sr-only">
                Full name
              </label>
              <User className="absolute left-3 top-3 h-5 w-5 text-gray-500" />
              <input
                id="name"
                name="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Full name"
              />
            </div>
            <div className="relative">
              <label htmlFor="companyName" className="sr-only">
                Company name
              </label>
              <Building2 className="absolute left-3 top-3 h-5 w-5 text-gray-500" />
              <input
                id="companyName"
                name="companyName"
                type="text"
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Company name"
              />
            </div>
            <div className="relative">
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-500" />
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-500" />
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                required
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="terms" className="text-gray-700">
                I agree to the{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLegalLinkClick('/legal/terms');
                  }}
                  className="text-blue-500 hover:text-blue-600"
                >
                  Terms of Service
                </a>{' '}
                and consent to the{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLegalLinkClick('/legal/privacy');
                  }}
                  className="text-blue-500 hover:text-blue-600"
                >
                  Privacy Policy
                </a>
              </label>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={authIsLoading}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                authIsLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {authIsLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </button>
          </div>
        </form>
      </div>

      <HelpButton variant="fixed" />
    </div>
  );
};
